/**
 * tick-hub 进项管理服务接口封装（泛域名门户首页「立即抵扣」链路专用精简版）
 *
 * 完整版在 jxgl 仓 src/api/tickHub.js（含登记门禁/税局会话弹窗广播等子系统
 * 专属逻辑）；本文件只承载首页卡片点击所需两个接口：
 *  - POST /api/jxgl/entry-click  进入判定（记录客户请求 + 返回是否已开通）
 *  - POST /api/intention         「我要试用」意向记录
 *
 * 会话协议与 jxgl 完全一致：
 *  - baseURL：VUE_APP_TICK_API（开发 /tickapi 代理，生产独立域名直连）
 *  - 请求头：X-Origin-Host（页面域名）+ x-token（preLogin 存的会话凭据）
 *    + cookie（withCredentials 自动携带）——tick-hub 服务端据此做统一会话校验
 *  - 响应结构：{ code, message, data, traceId }，code 为业务码（0 成功）
 *  - 40100（统一会话失效）→ 跳 cloud 登录页，登录后回跳当前页
 */

import Axios from 'axios'
import { Message } from 'element-ui'

export const TICK_BASE = process.env.VUE_APP_TICK_API || '/tickapi'

// tick-hub 业务码（ApiResponse.Codes）——本链路只关心 0 与 40100
export const TICK_CODES = {
    OK: 0,
    UNAUTHORIZED: 40100
}

// 本地联调判定：localhost / 127.0.0.1 下不跳 cloud 登录（该地址在本地是
// http://cloud 死链，会把调试页面劫持走）；凭据有效性交给服务端校验。
function isLocalDev () {
    return ['localhost', '127.0.0.1'].indexOf(window.location.hostname) > -1
}

/**
 * 统一会话头：X-Origin-Host（租户子域，服务端拼接 companyLogin 地址）
 * + x-token（preLogin 存的 localStorage.token）。cookie 由 withCredentials 自动携带。
 */
function resolveSessionHeaders () {
    return {
        'X-Origin-Host': window.location.hostname,
        'x-token': window.localStorage.token || ''
    }
}

const service = Axios.create({
    baseURL: TICK_BASE,
    timeout: 15000,
    // 跨子域携带 cookie（统一会话 x-token cookie）
    withCredentials: true
})

service.interceptors.request.use(
    config => {
        Object.assign(config.headers, resolveSessionHeaders())
        return config
    },
    error => Promise.reject(error)
)

service.interceptors.response.use(
    (response) => {
        const { data } = response
        // tick-hub 统一响应：{ code, message, data, traceId }
        if (data && typeof data.code === 'number') {
            if (data.code === TICK_CODES.OK) {
                return data
            }
            // 平台登录失效（统一会话）→ 跳 cloud 登录页，登录后回跳当前页；
            // 本地联调不跳（http://cloud 是死链）：留在当前页报错便于排查
            if (data.code === TICK_CODES.UNAUTHORIZED) {
                if (isLocalDev()) {
                    Message({
                        message: (data.message || '统一会话校验未通过（40100）') + '（本地联调不跳转）',
                        showClose: true,
                        type: 'error',
                        duration: 5 * 1000
                    })
                    return Promise.reject(data)
                }
                const cloudLogin = window.location.origin.replace(
                    window.location.host.split('.')[0], 'cloud') + '/login?redirect='
                    + encodeURIComponent(window.location.href)
                window.location.href = cloudLogin
                return Promise.reject(data)
            }
            Message.closeAll()
            Message({
                message: data.message || '进项管理服务返回异常',
                showClose: true,
                type: 'error',
                duration: 3 * 1000
            })
            return Promise.reject(data)
        }
        return data
    },
    err => {
        Message.closeAll()
        Message({
            message: '连接进项管理服务失败，请稍后重试',
            showClose: true,
            type: 'error',
            duration: 3 * 1000
        })
        return Promise.reject(err)
    }
)

/**
 * 「立即抵扣」进入判定：记录客户进入请求 + 返回是否已开通进项管理。
 * @param source 点击来源标识（如 'dealer-home-card'，可空——服务端兜底 jxgl-entry-click）
 * @returns {activated: boolean}（reject 时调用方放行直跳 /jxgl/home）
 */
export function jxglEntryClick (source) {
    return service.post('/api/jxgl/entry-click', { source: source })
}

/**
 * 「我要试用」意向记录（开通服务弹窗按钮）。
 * @param source 点击来源标识（如 'trial-click'）
 */
export function submitIntention (source) {
    return service.post('/api/intention', { source: source })
}
